import { PositionsSearchFilter } from "_graphql-types/graphql";
import { Chip, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  removeFilter,
  updateFilter,
} from "Reducers/globalSearchV2/globalSearchV2.actions";
import { getInvestmentFilters } from "Reducers/globalSearchV2/globalSearchV2.selectors";
import { useDispatch } from "src/react-redux";
import { FilterInterface } from "../FilterInterface";

const MIN_LENGTH = 3;

export function PositionsFilter() {
  const investmentFilters = useSelector(getInvestmentFilters);

  const positionsFilter = useMemo(() => {
    for (const filter of investmentFilters) {
      if (filter.type === "investment" && filter.data.type === "POSITIONS") {
        return filter.data.data;
      }
    }
  }, [investmentFilters]);

  const defaultFilter: PositionsSearchFilter = { mostRecent: true };

  const [localFilter, setLocalFilter] = useState<PositionsSearchFilter>(
    positionsFilter ?? defaultFilter
  );
  const [inputValue, setInputValue] = useState("");

  const dispatch = useDispatch();

  const items = [
    ...(localFilter.securityNames ?? []).map(v => ({ type: "name" as const, value: v })),
    ...(localFilter.tickers ?? []).map(v => ({ type: "ticker" as const, value: v })),
  ];

  const addItem = (raw: string) => {
    const value = raw.trim();
    if (!value) return;

    const isTicker = /^[A-Z]{1,5}$/.test(value);

    if (isTicker) {
      const existing = localFilter.tickers ?? [];
      if (!existing.includes(value)) {
        setLocalFilter({ ...localFilter, tickers: [...existing, value] });
      }
    } else {
      if (value.length < MIN_LENGTH) return;
      const existing = localFilter.securityNames ?? [];
      if (!existing.includes(value)) {
        setLocalFilter({ ...localFilter, securityNames: [...existing, value] });
      }
    }
    setInputValue("");
  };

  const removeItem = (type: "name" | "ticker", value: string) => {
    if (type === "name") {
      setLocalFilter({
        ...localFilter,
        securityNames: (localFilter.securityNames ?? []).filter(n => n !== value),
      });
    } else {
      setLocalFilter({
        ...localFilter,
        tickers: (localFilter.tickers ?? []).filter(t => t !== value),
      });
    }
  };

  const hasChips =
    (localFilter.securityNames?.length ?? 0) > 0 ||
    (localFilter.tickers?.length ?? 0) > 0;

  const pendingInputValid = (() => {
    const v = inputValue.trim();
    if (!v) return false;
    if (/^[A-Z]{1,5}$/.test(v)) return true;
    return v.length >= MIN_LENGTH;
  })();

  const set = () => {
    let filterToDispatch = localFilter;

    if (inputValue.trim()) {
      const value = inputValue.trim();
      const isTicker = /^[A-Z]{1,5}$/.test(value);
      if (isTicker) {
        const existing = filterToDispatch.tickers ?? [];
        if (!existing.includes(value)) {
          filterToDispatch = { ...filterToDispatch, tickers: [...existing, value] };
        }
      } else if (value.length >= MIN_LENGTH) {
        const existing = filterToDispatch.securityNames ?? [];
        if (!existing.includes(value)) {
          filterToDispatch = { ...filterToDispatch, securityNames: [...existing, value] };
        }
      }
      setLocalFilter(filterToDispatch);
      setInputValue("");
    }

    const valid =
      (filterToDispatch.securityNames?.length ?? 0) > 0 ||
      (filterToDispatch.tickers?.length ?? 0) > 0;
    if (!valid) return;

    dispatch(
      updateFilter({
        type: "investment",
        data: {
          type: "investment",
          data: {
            type: "POSITIONS",
            data: filterToDispatch,
          },
        },
      })
    );
  };

  const reset = () => {
    setLocalFilter(defaultFilter);
    setInputValue("");
    dispatch(
      removeFilter({
        type: "investment",
        data: {
          type: "investment",
          data: {
            type: "POSITIONS",
            data: defaultFilter,
          },
        },
      })
    );
  };

  return (
    <FilterInterface
      label="Positions"
      count={items.length}
      reset={reset}
      set={set}
      disabled={!hasChips && !pendingInputValid}
      menuClass="filters__menu-dropdown"
      minWidth={300}
    >
      <div className="main-dropdown__menu-body">
        <label className="main-input__label font-12">
          Security name or ticker
        </label>
        <TextField
          size="small"
          fullWidth
          placeholder="e.g. Corning, NVDA"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter") {
              e.preventDefault();
              addItem(inputValue);
            }
          }}
          helperText="Press Enter to add. Uppercase 1-5 chars = ticker, otherwise name (min 3 chars)."
          sx={{ mb: 1 }}
        />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
          {items.map(({ type, value }) => (
            <Chip
              key={`${type}:${value}`}
              label={type === "ticker" ? `$${value}` : value}
              size="small"
              variant="outlined"
              color={type === "ticker" ? "primary" : "default"}
              onDelete={() => removeItem(type, value)}
            />
          ))}
        </div>
      </div>
    </FilterInterface>
  );
}
